// pages/users/UserRolePage.jsx
// Generic page — used by AllUsers, Admins, SuperDistributors, Distributors,
// SubDistributors, Retailers, Clients with just different `role` prop.

import { useState } from 'react';
import { Plus, Download, Trash2, RefreshCw } from 'lucide-react';
import { useUsers, C } from '../../hooks/useUsers.js';
import { userController } from '../../controllers/userController.js';
import { ROLE_LABELS } from '../../models/User.js';
import { UserTable, Pagination, FilterBar, UserFormModal, UserViewModal } from '../../components/users/UserTable.jsx';

const EMPTY_FORM = {
  firstName:'', lastName:'', email:'', phone:'', password:'',
  role:'client', commissionRate:0, address:{}
};

export default function UserRolePage({ role, title, roleOptions, accentColor }) {
  const accent = accentColor || '#3b82f6';

  const {
    users, loading, total, totalPages, page, setPage,
    filters, handleFilter, sort, handleSort,
    selected, toggleSelect, toggleAll, setSelected,
    refresh,
  } = useUsers({ role });

  const [showAdd,  setShowAdd]  = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showView, setShowView] = useState(false);
  const [target,   setTarget]   = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors,   setErrors]   = useState([]);
  const [submitting, setSub]    = useState(false);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors([]);
  };

  // ── Add ─────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setFormData({ ...EMPTY_FORM, role: role || 'client' });
    setErrors([]);
    setShowAdd(true);
  };
  const handleAdd = async e => {
    e.preventDefault();
    setSub(true); setErrors([]);
    const res = await userController.create(formData);
    if (res.success) { setShowAdd(false); refresh(); }
    else setErrors(res.errors || [res.message]);
    setSub(false);
  };

  // ── Edit ────────────────────────────────────────────────────────────────
  const openEdit = user => {
    setTarget(user);
    setFormData({ ...user, address: user.address || {} });
    setErrors([]);
    setShowEdit(true);
    setShowView(false);
  };
  const handleEdit = async e => {
    e.preventDefault();
    setSub(true); setErrors([]);
    const res = await userController.update(target.id, formData);
    if (res.success) { setShowEdit(false); refresh(); }
    else setErrors(res.errors || [res.message]);
    setSub(false);
  };

  // ── View ────────────────────────────────────────────────────────────────
  const openView = user => { setTarget(user); setShowView(true); };

  // ── Delete ──────────────────────────────────────────────────────────────
  const handleDelete = async user => {
    if (!window.confirm(`Delete ${user.fullName}?`)) return;
    await userController.delete(user.id);
    refresh();
  };
  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} users?`)) return;
    await userController.bulkDelete(selected);
    setSelected([]);
    refresh();
  };

  // ── Block / Verify ──────────────────────────────────────────────────────
  const handleBlock  = async user => { await userController.toggleBlock(user.id); refresh(); };
  const handleVerify = async user => { await userController.verify(user.id); refresh(); };

  // ── Stats ───────────────────────────────────────────────────────────────
  const stats = {
    total,
    active:   users.filter(u => u.isActive && !u.isBlocked).length,
    blocked:  users.filter(u => u.isBlocked).length,
    unverified: users.filter(u => !u.isVerified).length,
  };

  return (
    <div style={C.page}>
      {/* Header */}
      <div style={C.header}>
        <div>
          <h1 style={{ ...C.title, borderLeft:`4px solid ${accent}`, paddingLeft:'12px' }}>{title}</h1>
          <p style={{ fontSize:'13px', color:'#64748b', margin:'4px 0 0 16px' }}>{total} total users</p>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          {selected.length > 0 && (
            <button style={{ ...C.btn, ...C.btnDanger }} onClick={handleBulkDelete}>
              <Trash2 size={15} /> Delete ({selected.length})
            </button>
          )}
          <button style={{ ...C.btn, ...C.btnSec }} onClick={() => userController.exportCSV(users)}>
            <Download size={15} /> Export
          </button>
          <button style={{ ...C.btn, ...C.btnSec }} onClick={refresh}>
            <RefreshCw size={15} />
          </button>
          <button style={{ ...C.btn, backgroundColor: accent, color:'#fff' }} onClick={openAdd}>
            <Plus size={15} /> Add User
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={C.statsBar}>
        {[
          { label:'Total', value: total,             color: accent },
          { label:'Active', value: stats.active,     color:'#10b981' },
          { label:'Blocked', value: stats.blocked,   color:'#ef4444' },
          { label:'Unverified', value: stats.unverified, color:'#f59e0b' },
        ].map(s => (
          <div key={s.label} style={{ ...C.statCard, borderLeftColor: s.color }}>
            <div style={C.statLabel}>{s.label}</div>
            <div style={{ ...C.statVal, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <FilterBar filters={filters} onFilter={handleFilter}
        extra={
          !role && (
            <select style={C.select} value={filters.role||'All'} onChange={e => handleFilter('role', e.target.value)}>
              <option value="All">All Roles</option>
              {['admin','super_distributor','distributor','subdistributor','retailer','client'].map(r => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          )
        }
      />

      {/* Table */}
      <div style={C.card}>
        {loading ? (
          <div style={{ padding:'60px', textAlign:'center', color:'#94a3b8' }}>
            <RefreshCw size={28} style={{ animation:'spin 1s linear infinite' }} />
            <div style={{ marginTop:'12px' }}>Loading…</div>
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding:'60px', textAlign:'center', color:'#94a3b8' }}>
            <div style={{ fontSize:'40px', marginBottom:'12px' }}>👤</div>
            <div style={{ fontSize:'16px', fontWeight:'500' }}>No users found</div>
            <div style={{ fontSize:'13px', marginTop:'6px' }}>Try adjusting your filters</div>
          </div>
        ) : (
          <>
            <UserTable
              users={users} sort={sort} onSort={handleSort}
              selected={selected} onToggle={toggleSelect} onToggleAll={toggleAll}
              onView={openView} onEdit={openEdit} onDelete={handleDelete}
              onBlock={handleBlock} onVerify={handleVerify}
            />
            <Pagination page={page} totalPages={totalPages} total={total} limit={10} onPage={setPage} />
          </>
        )}
      </div>

      {/* Add Modal */}
      <UserFormModal
        show={showAdd} title="Add New User" formData={formData}
        onChange={handleInputChange} onSubmit={handleAdd} onClose={() => setShowAdd(false)}
        submitting={submitting} errors={errors} roleOptions={roleOptions}
      />

      {/* Edit Modal */}
      <UserFormModal
        show={showEdit} title="Edit User" formData={formData}
        onChange={handleInputChange} onSubmit={handleEdit} onClose={() => setShowEdit(false)}
        submitting={submitting} errors={errors} roleOptions={roleOptions}
      />

      {/* View Modal */}
      <UserViewModal
        show={showView} user={target} onClose={() => setShowView(false)}
        onEdit={openEdit} onBlock={handleBlock} onVerify={handleVerify}
      />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}